import type { FurnitureObject } from '../types';

const bounded = (value: number, min: number, max: number, fallback: number) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : fallback));

/** Keep in-progress numeric edits safe for the renderer without changing the saved draft. */
export function previewWardrobe(draft: FurnitureObject): FurnitureObject {
  const width = bounded(draft.width, 300, 10000, 2400);
  const height = bounded(draft.height, 500, 6000, 2600);
  const depth = bounded(draft.depth, 250, 1500, 600);
  const panelThickness = bounded(draft.panelThickness, 6, 60, 18);
  const sections = draft.sections.slice(0, 8);
  const widths = sections.map((section) => bounded(section.width, 1, width, 1));
  const total = widths.reduce((sum, value) => sum + value, 0) || 1;
  return {
    ...draft, width, height, depth, panelThickness,
    backThickness: bounded(draft.backThickness, 2, 60, 3),
    intermediateTopCount: Math.floor(bounded(draft.intermediateTopCount ?? 0, 0, 12, 0)),
    slope: draft.slope ? {
      leftHeight: bounded(draft.slope.leftHeight, 500, 6000, height),
      rightHeight: bounded(draft.slope.rightHeight, 500, 6000, height),
    } : undefined,
    drawers: { ...draft.drawers, frontHeight: bounded(draft.drawers.frontHeight, 80, 400, 160), gap: bounded(draft.drawers.gap, 1, 20, 3), depth: bounded(draft.drawers.depth, 100, depth, depth / 2) },
    doors: { ...draft.doors, count: Math.floor(bounded(draft.doors.count, 0, 8, 0)), thickness: bounded(draft.doors.thickness, 6, 60, 18), gap: bounded(draft.doors.gap, 1, 20, 3) },
    sections: sections.map((section, index) => ({
      ...section,
      width: widths[index] / total * width,
      shelfCount: Math.floor(bounded(section.shelfCount, 0, 20, 0)),
      drawerCount: Math.floor(bounded(section.drawerCount, 0, 12, 0)),
      shelfThickness: bounded(section.shelfThickness ?? panelThickness, 6, 60, panelThickness),
      drawerDepth: bounded(section.drawerDepth ?? draft.drawers.depth, 100, depth, depth / 2),
      drawerExtension: bounded(section.drawerExtension ?? 75, 25, 75, 75),
      drawerHeights: (section.drawerHeights ?? []).slice(0, 12).map((value) => bounded(value, 80, 400, draft.drawers.frontHeight)),
      shelfHeights: (section.shelfHeights ?? []).slice(0, 20).map((value) => bounded(value, panelThickness + 1, height - panelThickness, height / 2)),
      intermediateHeights: (section.intermediateHeights ?? []).slice(0, 12).map((value) => bounded(value, panelThickness + 1, height - panelThickness, height / 2)),
      railHeightFromFloor: bounded(section.railHeightFromFloor ?? height * .72, 150, height - panelThickness, height * .72),
    })),
  };
}
